/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { FormattedDate, FormattedMessage, useIntl } from 'react-intl'
import SelectableRow from '../../../../client/js/components/selection/SelectableRow'
import CustomerLink from '../customer/CustomerLink'
import QuickMenu from '../QuickMenu'
import Ref from '../Ref'
import LeaseStatusBadge from './LeaseStatusBadge'
import {
  getLeaseInterventionListLink,
  getLeaseInvoiceListLink,
  getLeaseLink,
  getLeaseManagementReportListLink,
  getLeaseRentalReportListLink
} from '../../lib/links/leaseLinks'
import { Menu, Table, Text } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { Link } from 'react-router-dom'
import EstatePhoto from '../estate/EstatePhoto'
import Amount from '../transaction/Amount'
import RequirePerm from '../../../auth/components/RequirePerm'

export type LeaseListItemProps = {
  imageHeight?: number,
  imageWidth?: number,
  item: Lease
}

function LeaseListItem (props: LeaseListItemProps) {
  const {
    imageHeight = 90,
    imageWidth = 160,
    item
  } = props

  const intl = useIntl()

  return (
    <SelectableRow value={item.id}>
      <Table.Td>
        <LeaseStatusBadge lease={item} />
      </Table.Td>
      <Table.Td>
        <EstatePhoto
          photos={item.Estate.Photos}
          height={imageHeight}
          width={imageWidth}
        />
      </Table.Td>
      <Table.Td>
        <AnchorLink to={getLeaseLink(item.id)}>
          <Ref value={item.reference} />
        </AnchorLink>
        <Text size="xs">
          {item.managed
            ? intl.formatMessage({
              id: 'managed',
              defaultMessage: 'Géré'
            })
            : intl.formatMessage({
              id: 'not_managed',
              defaultMessage: 'Non géré'
            })}
        </Text>
      </Table.Td>
      <Table.Td>
        {item.Lessor
          ? <CustomerLink customer={item.Lessor} />
          : null}
      </Table.Td>
      <Table.Td>
        {item.Tenant
          ? <CustomerLink customer={item.Tenant} />
          : null}
      </Table.Td>
      <Table.Td ta="end">
        <Amount value={item.rent} inherit withCurrency />
      </Table.Td>
      <Table.Td>
        {item.startDate
          ? <FormattedDate value={item.startDate} />
          : null}
      </Table.Td>
      <Table.Td>
        {item.endDate
          ? <FormattedDate value={item.endDate} />
          : null}
        {item.billingEndDate
          ? <Text size="xs">
            <FormattedMessage
              id="billing_end_on"
              defaultMessage="Fin de facturation le {date}"
              values={{ date: <FormattedDate value={item.billingEndDate} /> }}
            />
          </Text>
          : null}
        {item.noticeDate
          ? <Text size="xs">
            <FormattedMessage
              id="notice_on_date"
              defaultMessage="Préavis le {date}"
              values={{ date: <FormattedDate value={item.noticeDate} /> }}
            />
          </Text>
          : null}
      </Table.Td>
      <Table.Td>
        <QuickMenu buttonProps={{ size: 'xs' }}>
          <RequirePerm
            permissions={['read:invoice']}
            render={() => (
              <Menu.Item
                component={Link}
                to={getLeaseInvoiceListLink(item.id)}
              >
                {intl.formatMessage({
                  id: 'invoices',
                  defaultMessage: 'Factures'
                })}
              </Menu.Item>
            )}
          />
          <RequirePerm
            permissions={['read:intervention']}
            render={() => (
              <Menu.Item
                component={Link}
                to={getLeaseInterventionListLink(item.id)}
              >
                {intl.formatMessage({
                  id: 'interventions',
                  defaultMessage: 'Interventions'
                })}
              </Menu.Item>
            )}
          />
          <RequirePerm
            permissions={['read:management-report']}
            render={() => (
              <Menu.Item
                component={Link}
                to={getLeaseManagementReportListLink(item.id)}
              >
                {intl.formatMessage({
                  id: 'management_reports',
                  defaultMessage: 'Rapports de gérance'
                })}
              </Menu.Item>
            )}
          />
          <RequirePerm
            permissions={['read:management-report']}
            render={() => (
              <Menu.Item
                component={Link}
                to={getLeaseRentalReportListLink(item.id)}
              >
                {intl.formatMessage({
                  id: 'rental_reports',
                  defaultMessage: 'Rapports de location'
                })}
              </Menu.Item>
            )}
          />
          <Menu.Divider />
          <Menu.Item
            component={Link}
            to={`/api/leases/${item.id}/file`}
            rel="noopener"
            target="_blank"
          >
            {intl.formatMessage({
              id: 'download_contract',
              defaultMessage: 'Télécharger le contrat'
            })}
          </Menu.Item>
          <Menu.Item
            component={Link}
            to={`/api/leases/${item.id}/template`}
            rel="noopener"
            target="_blank"
          >
            {intl.formatMessage({
              id: 'download_template',
              defaultMessage: 'Télécharger le modèle'
            })}
          </Menu.Item>
        </QuickMenu>
      </Table.Td>
    </SelectableRow>
  )
}

export default LeaseListItem
